import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  TrendingUp, Users, PhoneCall, CheckCircle2, AlertTriangle, Euro,
  Loader2, Clock, BarChart3, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtEur = (v) => `${Number(v || 0).toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;
const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;

const KpiCard = ({ icon: Icon, label, value, sub, color = 'slate', testid }) => (
  <Card className="rounded-2xl border-slate-100" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
        <Icon className={`w-4 h-4 text-${color}-500`} /> {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-800">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const compareToAvg = (val, avg) => {
  if (avg == null) return null;
  const diff = val - avg;
  if (Math.abs(diff) < 0.5) return { icon: Minus, color: 'text-slate-400', label: '≈ média' };
  if (diff > 0) return { icon: ArrowUpRight, color: 'text-emerald-600', label: `+${diff.toFixed(1)}pp` };
  return { icon: ArrowDownRight, color: 'text-rose-600', label: `${diff.toFixed(1)}pp` };
};

export const LeadsPerformancePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodDays, setPeriodDays] = useState('30');
  const [partnerId, setPartnerId] = useState('all');
  const [partners, setPartners] = useState([]);
  const [sortKey, setSortKey] = useState('pct_converted');

  const isAllowed = ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice', 'chefe_equipa'].includes(user?.role);

  useEffect(() => {
    if (!isAllowed) return;
    axios.get(`${API_URL}/leads/assignable-partners`).then(r => setPartners(r.data || [])).catch(() => {});
  }, [isAllowed]);

  useEffect(() => {
    if (!isAllowed) return;
    setLoading(true);
    const params = { period_days: periodDays };
    if (partnerId && partnerId !== 'all') params.partner_id = partnerId;
    axios.get(`${API_URL}/leads/performance`, { params })
      .then(r => setData(r.data))
      .catch(() => toast.error('Erro a carregar performance'))
      .finally(() => setLoading(false));
  }, [periodDays, partnerId, isAllowed]);

  const sorted = useMemo(() => {
    if (!data?.by_commercial) return [];
    const list = [...data.by_commercial];
    list.sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
    return list;
  }, [data, sortKey]);

  const teamAvg = data?.team_avg || {};

  if (!isAllowed) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <p className="text-lg font-semibold">Sem acesso</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="leads-performance-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-indigo-500" /> Performance de Leads
            </h1>
            <p className="text-sm text-slate-500 mt-1">Eficácia e produtividade dos comerciais.</p>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={periodDays} onValueChange={setPeriodDays}>
              <SelectTrigger className="w-32" data-testid="period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
                <SelectItem value="180">Últimos 6 meses</SelectItem>
                <SelectItem value="365">Último ano</SelectItem>
              </SelectContent>
            </Select>
            <Select value={partnerId} onValueChange={setPartnerId}>
              <SelectTrigger className="w-48" data-testid="partner-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os parceiros</SelectItem>
                {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiCard testid="kpi-total" icon={Users} label="Leads" value={data?.totals.leads_total || 0} color="indigo" />
              <KpiCard testid="kpi-contacted" icon={PhoneCall} label="Contactadas" value={`${data?.totals.leads_contacted || 0} · ${fmtPct(data?.totals.pct_contacted)}`} color="cyan" />
              <KpiCard testid="kpi-converted" icon={CheckCircle2} label="Convertidas" value={`${data?.totals.leads_convertida || 0} · ${fmtPct(data?.totals.pct_converted)}`} color="emerald" />
              <KpiCard testid="kpi-revenue" icon={Euro} label="€ Convertido" value={fmtEur(data?.totals.valor_convertido_eur)} sub={`Ticket médio ${fmtEur(data?.totals.ticket_medio_eur)}`} color="emerald" />
              <KpiCard testid="kpi-untouched" icon={AlertTriangle} label="Por trabalhar" value={`${data?.totals.leads_untouched || 0} · ${fmtPct(data?.totals.pct_untouched)}`} color="rose" />
              <KpiCard testid="kpi-overdue" icon={Clock} label="Follow-ups atrasados" value={data?.totals.overdue_followups || 0} color="amber" />
            </div>

            {/* Conversion ranking chart */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Ranking — Taxa de Conversão (%)</CardTitle>
                <span className="text-xs text-slate-400">Média da equipa: {fmtPct(teamAvg.pct_converted)}</span>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 24 + Math.max(120, sorted.length * 28) }}>
                  <ResponsiveContainer>
                    <BarChart data={sorted.map(c => ({ name: c.name.split(' ').slice(0, 2).join(' '), pct: c.pct_converted, fill: c.pct_converted >= teamAvg.pct_converted ? '#10b981' : '#f97316' }))} layout="vertical" margin={{ left: 80, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(v) => `${v}%`} />
                      <YAxis dataKey="name" type="category" width={140} />
                      <Tooltip formatter={(v) => [`${v}%`, 'Conversão']} />
                      <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
                        {sorted.map((c, i) => (
                          <Cell key={i} fill={c.pct_converted >= teamAvg.pct_converted ? '#10b981' : '#f97316'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Tabela detalhada</CardTitle>
                <Select value={sortKey} onValueChange={setSortKey}>
                  <SelectTrigger className="w-56" data-testid="sort-key-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pct_converted">Conversão %</SelectItem>
                    <SelectItem value="pct_converted_among_contacted">Conversão entre contactadas</SelectItem>
                    <SelectItem value="valor_convertido_eur">€ Convertido</SelectItem>
                    <SelectItem value="receita_por_lead_eur">€ por Lead</SelectItem>
                    <SelectItem value="pct_pickup_rate">Taxa Atendimento</SelectItem>
                    <SelectItem value="leads_total">Volume (atribuídas)</SelectItem>
                    <SelectItem value="contacts_total">Chamadas registadas</SelectItem>
                    <SelectItem value="pct_untouched">% Por trabalhar (pior 1º)</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent className="px-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="performance-table">
                  <thead className="bg-slate-50 text-xs">
                    <tr>
                      <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Comercial</th>
                      <th className="text-right px-2 py-2">Total</th>
                      <th className="text-right px-2 py-2 bg-rose-50/30">Por trab.</th>
                      <th className="text-right px-2 py-2">Contact.</th>
                      <th className="text-right px-2 py-2">Reag.</th>
                      <th className="text-right px-2 py-2">Visitas</th>
                      <th className="text-right px-2 py-2 bg-emerald-50/30">Conv.</th>
                      <th className="text-right px-2 py-2">S/ Int.</th>
                      <th className="text-right px-2 py-2 bg-emerald-50/30">Conv %</th>
                      <th className="text-right px-2 py-2">Conv/Cont %</th>
                      <th className="text-right px-2 py-2">Atend %</th>
                      <th className="text-right px-2 py-2">Tent./lead</th>
                      <th className="text-right px-2 py-2">1ª chamada</th>
                      <th className="text-right px-2 py-2 bg-emerald-50/30">€ Conv.</th>
                      <th className="text-right px-2 py-2">€/lead</th>
                      <th className="text-right px-2 py-2">Última act.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.length === 0 && (
                      <tr><td colSpan={16} className="text-center py-8 text-slate-400">Sem dados no período</td></tr>
                    )}
                    {sorted.map((c) => {
                      const lastAct = c.last_activity ? new Date(c.last_activity).toLocaleString('pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
                      const cmp = compareToAvg(c.pct_converted, teamAvg.pct_converted);
                      const CmpIcon = cmp?.icon;
                      return (
                        <tr key={c.user_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-${c.user_id}`}>
                          <td className="px-3 py-2 sticky left-0 bg-white">
                            <button onClick={() => navigate(`/leads/pipeline?assigned_to=${c.user_id}`)} className="font-semibold text-slate-800 hover:text-indigo-600 hover:underline text-left">
                              {c.name}
                            </button>
                            <div className="text-[11px] text-slate-400">{c.partner_name}</div>
                          </td>
                          <td className="text-right px-2 py-2 font-semibold">{c.leads_total}</td>
                          <td className="text-right px-2 py-2 bg-rose-50/20">
                            {c.leads_untouched > 0 ? (
                              <span className={`${c.untouched_age_days > 7 ? 'text-rose-700 font-semibold' : 'text-slate-700'}`} title={`Mais antiga: ${c.untouched_age_days}d`}>
                                {c.leads_untouched} {c.untouched_age_days > 0 && <span className="text-[10px] text-rose-500">({c.untouched_age_days}d)</span>}
                              </span>
                            ) : '0'}
                          </td>
                          <td className="text-right px-2 py-2">{c.leads_contacted_count}</td>
                          <td className="text-right px-2 py-2">{c.leads_reagendada}</td>
                          <td className="text-right px-2 py-2">{c.contacts_visita_marcada}</td>
                          <td className="text-right px-2 py-2 bg-emerald-50/20 font-semibold">{c.leads_convertida}</td>
                          <td className="text-right px-2 py-2">{c.leads_sem_interesse}</td>
                          <td className="text-right px-2 py-2 bg-emerald-50/20 font-bold">
                            <div className="flex items-center justify-end gap-1">
                              <span>{fmtPct(c.pct_converted)}</span>
                              {CmpIcon && <CmpIcon className={`w-3 h-3 ${cmp.color}`} />}
                            </div>
                          </td>
                          <td className="text-right px-2 py-2 text-slate-600">{fmtPct(c.pct_converted_among_contacted)}</td>
                          <td className="text-right px-2 py-2 text-slate-600">{fmtPct(c.pct_pickup_rate)}</td>
                          <td className="text-right px-2 py-2 text-slate-600">{c.avg_attempts_per_lead || 0}</td>
                          <td className="text-right px-2 py-2 text-slate-600">{c.avg_time_to_first_contact_h != null ? `${c.avg_time_to_first_contact_h}h` : '—'}</td>
                          <td className="text-right px-2 py-2 bg-emerald-50/20 font-semibold">{fmtEur(c.valor_convertido_eur)}</td>
                          <td className="text-right px-2 py-2">{fmtEur(c.receita_por_lead_eur)}</td>
                          <td className="text-right px-2 py-2 text-[11px] text-slate-500">{lastAct}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Legenda */}
            <div className="text-xs text-slate-400 italic flex flex-wrap gap-3">
              <span>📍 Click no nome do comercial para ver as leads dele.</span>
              <span>🟢 Verde = acima da média da equipa.</span>
              <span>🟧 Laranja = abaixo da média.</span>
              <span>Por trab. = leads atribuídas sem qualquer contacto registado. (Xd) = idade da mais antiga.</span>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default LeadsPerformancePage;
